import React, { useState, useCallback, useEffect, useRef } from 'react';

import {
    enableBodyScroll,
    disableBodyScroll,
    clearAllBodyScrollLocks,
} from 'body-scroll-lock';

import { className, isMobile } from 'aplazame-design-system';
import TransitionLink from 'aplazame-design-system/src/components/TransitionLink';

import style from './style.module.scss';
import transitionStyle from 'aplazame-design-system/src/scss/transitions.module.scss';

function findActiveItem(items, activeDocApiId) {
    let item = items.find((i) => i.docApi.id === activeDocApiId);
    if (!item) {
        items.forEach((thisItem) => {
            let subitem = thisItem.subitems.find(
                (i) => i.docApi.id === activeDocApiId
            );
            if (subitem) {
                item = {
                    ...subitem,
                    text: `${thisItem.text} / ${subitem.text}`,
                };
            }
        });
    }

    return item;
}

export default function DocumentationMenu({
    items,
    activeDocApiId,
    sectionRef,
}) {
    const scrollRef = useRef();

    const [showMenu, setShowMenu] = useState(isMobile() ? false : true);
    const [showSidebar, setShowSidebar] = useState(true);
    const [activeItem] = useState(findActiveItem(items, activeDocApiId));

    useEffect(() => {
        if (!scrollRef.current || !isMobile()) {
            return;
        }

        if (showMenu) {
            disableBodyScroll(scrollRef.current);
        } else {
            enableBodyScroll(scrollRef.current);
        }
    }, [showMenu, scrollRef]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const changeShowMenu = () => {
            setShowMenu(isMobile() ? false : true);
        };

        window.addEventListener('resize', changeShowMenu);

        return () => {
            clearAllBodyScrollLocks();
            window.removeEventListener('resize', changeShowMenu);
        };
    }, [setShowMenu]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const changeShowSidebar = () => {
            if (!sectionRef.current || isMobile()) {
                setShowSidebar(true);
                return;
            }

            setShowSidebar(
                window.scrollY <
                    sectionRef.current.offsetHeight + 80 - window.innerHeight
            );
        };

        window.addEventListener('resize', changeShowSidebar);
        window.addEventListener('scroll', changeShowSidebar);

        return () => {
            window.removeEventListener('resize', changeShowSidebar);
            window.removeEventListener('scroll', changeShowSidebar);
        };
    }, [setShowSidebar, sectionRef]);

    const toggleMenu = useCallback(() => {
        if (!isMobile()) {
            return;
        }

        setShowMenu(!showMenu);
    }, [showMenu, setShowMenu]);

    return (
        <>
            <div {...className(`${style.space} d-lg-none`)} />
            <div
                {...className(
                    `d-flex flex-column ${style.wrapper} ${
                        showMenu ? style['wrapper--show'] : ''
                    } ${showSidebar ? style['wrapper--show-lg'] : ''}`
                )}
            >
                <button
                    {...className(`d-lg-none body ${style.button}`)}
                    onClick={toggleMenu}
                >
                    {activeItem ? activeItem.text : null}
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M20.9708 9.2128C21.5256 8.70036 21.5256 7.82379 20.9708 7.31135C20.4749 6.85323 19.7102 6.85323 19.2143 7.31135L12 13.9759L4.78571 7.31135C4.2898 6.85323 3.52507 6.85323 3.02916 7.31135C2.47444 7.82379 2.47444 8.70036 3.02916 9.2128L11.3214 16.8731C11.7046 17.2271 12.2954 17.2271 12.6786 16.8731L20.9708 9.2128Z"
                            fill="#231F20"
                        />
                    </svg>
                </button>
                <div
                    ref={scrollRef}
                    {...className(
                        `${style.menu} ${showMenu ? '' : 'd-none'} d-lg-block`
                    )}
                >
                    <ul {...className(`list-unstyled ${style['menu__list']}`)}>
                        {items.map((link, i) => (
                            <li key={i}>
                                <TransitionLink
                                    {...className(
                                        `body ${style['menu__item']} ${transitionStyle.link}`
                                    )}
                                    activeClassName={
                                        style['menu__item--active']
                                    }
                                    to={link.docApi.slug}
                                    title={link.text}
                                >
                                    {link.text}
                                </TransitionLink>
                                {link.subitems && link.subitems.length ? (
                                    <ul
                                        {...className(
                                            [
                                                'list-unstyled',
                                                link.docApi.id ===
                                                    activeDocApiId ||
                                                link.subitems
                                                    .map(
                                                        (subitem) =>
                                                            subitem.docApi.id
                                                    )
                                                    .indexOf(activeDocApiId) !==
                                                    -1
                                                    ? ''
                                                    : 'd-none',
                                                style['menu__list'],
                                            ].join(' ')
                                        )}
                                    >
                                        {link.subitems.map((sublink, ii) => (
                                            <li key={ii}>
                                                {sublink.isPage ? (
                                                    <TransitionLink
                                                        {...className(
                                                            `body ${style['menu__item']}  ${style['menu__item--child']} ${transitionStyle.link}`
                                                        )}
                                                        activeClassName={
                                                            style[
                                                                'menu__item--active'
                                                            ]
                                                        }
                                                        to={`${sublink.docApi.slug}`}
                                                        title={sublink.text}
                                                    >
                                                        {`— ${sublink.text}`}
                                                    </TransitionLink>
                                                ) : (
                                                    <a
                                                        {...className(
                                                            `body ${style['menu__item']} ${style['menu__item--child']} ${transitionStyle.link}`
                                                        )}
                                                        href={`${
                                                            link.subitems
                                                                .map(
                                                                    (subitem) =>
                                                                        subitem
                                                                            .docApi
                                                                            .id
                                                                )
                                                                .indexOf(
                                                                    activeDocApiId
                                                                ) !== -1
                                                                ? sublink.docApi
                                                                      .slug
                                                                : ''
                                                        }#${sublink.slug}`}
                                                        title={sublink.text}
                                                        onClick={toggleMenu}
                                                    >
                                                        {sublink.text}
                                                    </a>
                                                )}
                                                {sublink.subitems.length ? (
                                                    <ul
                                                        {...className(
                                                            `list-unstyled ${
                                                                sublink.docApi
                                                                    .id ===
                                                                activeDocApiId
                                                                    ? ''
                                                                    : 'd-none'
                                                            } ${
                                                                style[
                                                                    'menu__list'
                                                                ]
                                                            }`
                                                        )}
                                                    >
                                                        {sublink.subitems.map(
                                                            (
                                                                subsublink,
                                                                iii
                                                            ) => (
                                                                <li key={iii}>
                                                                    <a
                                                                        {...className(
                                                                            `body ${style['menu__item']} ${style['menu__item--subchild']} ${transitionStyle.link}`
                                                                        )}
                                                                        href={`#${subsublink.slug}`}
                                                                        title={
                                                                            subsublink.text
                                                                        }
                                                                        onClick={
                                                                            toggleMenu
                                                                        }
                                                                    >
                                                                        {
                                                                            subsublink.text
                                                                        }
                                                                    </a>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </>
    );
}
